import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	switchMap,
	tap,
} from 'rxjs/operators';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

interface IPromptuaryHistory {
	createdAt: string;
	serviceEvolution: string;
	indices?: number[][];
}

interface ISearchSubject {
	search: string;
	page: number;
}

@Component({
	selector: 'vc-history',
	templateUrl: './vc-history.component.html',
	styleUrls: ['./vc-history.component.scss'],
})
export class VCHistoryComponent implements OnInit, OnDestroy {
	public currentPage = 1;
	@Input() contactId = null;
	@Input() hasBackground = true;
	@Input() useAccordion = false;
	@Input() extendedList = true;
	@Input() mode: 'vc' | 'appointment' = 'vc';
	@Output() redirectToDocumentaryRegister = new EventEmitter();
	constructor(
		private fb: FormBuilder,
		private promptuaryService: PromptuaryService,
		private atService: ActionTrackerService
	) {}

	public searchResults: IPromptuaryHistory[] = [];
	public form: FormGroup;
	public total = 0;
	public isLoading = true;
	public isLoaded = false;
	public resultsHasEnded = false;
	public isLoadingMore = false;
	public searchSubject: Subject<ISearchSubject> = new Subject();
	public searchSubscription = null;

	ngOnInit(): void {
		this.form = this.fb.group({
			search: '',
		});

		this.createSearchDebounce();
		this.searchSubject.next(this.form.value.search);
	}

	createSearchDebounce() {
		this.searchSubscription = this.searchSubject
			.pipe(
				tap(() => {
					this.isLoading = true;
					this.isLoadingMore = true;
				}),
				debounceTime(1200),
				distinctUntilChanged(),
				switchMap(() => {
					return this.promptuaryService.getPromptuaryHistory({
						q: this.form.value.search || '',
						page: this.currentPage,
						limit: 3,
						customerId: this.contactId,
					});
				})
			)
			.subscribe(
				(res) => {
					this.total = res.total;
					this.searchResults = [...this.searchResults, ...res.items];
					if (this.total === this.searchResults.length) {
						this.resultsHasEnded = true;
					}
					if (!this.isLoaded && this.total > 0) {
						this.atService.eventTracker(
							this.mode === 'vc' ? SCOPE_TYPE.Session : SCOPE_TYPE.Appointment,
							this.mode === 'vc'
								? SUBSCOPE_TYPE.Conference
								: SUBSCOPE_TYPE.Details,
							ITEM_TYPE.VCSearch,
							INTERACTION_TYPE.Seen
						);
					}

					this.isLoaded = true;
					this.isLoadingMore = false;
					this.isLoading = false;
					this.atService.eventTracker(
						this.mode === 'vc' ? SCOPE_TYPE.Session : SCOPE_TYPE.Appointment,
						this.mode === 'vc'
							? SUBSCOPE_TYPE.Conference
							: SUBSCOPE_TYPE.Details,
						ITEM_TYPE.VCSearch,
						INTERACTION_TYPE.Success
					);
				},
				() => {
					this.isLoaded = true;
					this.isLoadingMore = false;
					this.isLoading = false;
					this.atService.eventTracker(
						this.mode === 'vc' ? SCOPE_TYPE.Session : SCOPE_TYPE.Appointment,
						this.mode === 'vc'
							? SUBSCOPE_TYPE.Conference
							: SUBSCOPE_TYPE.Details,
						ITEM_TYPE.VCSearch,
						INTERACTION_TYPE.Error
					);
				}
			);
	}

	public formatTitle(item): string {
		const date = DateTime.fromISO(item.createdAt).toFormat('dd/MM/yyyy');
		return `${date} ${item.levelDescription ? item.levelDescription : ''}`;
	}

	cleanSearch() {
		this.form.reset();
		this.search();
	}

	search($event?) {
		if ($event?.length > 119) {
			this.form.setValue({ search: $event.substring(0, 120) });
			this.form.updateValueAndValidity();
			return;
		}
		this.resultsHasEnded = false;
		this.searchResults = [];
		this.currentPage = 1;
		this.isLoaded = false;
		this.searchSubject.next({
			search: this.form.value.search,
			page: this.currentPage,
		});
	}

	loadMore() {
		this.currentPage++;
		this.searchSubject.next({
			search: this.form.value.search,
			page: this.currentPage,
		});
	}

	ngOnDestroy(): void {
		this.searchSubscription.unsubscribe();
	}
}
